// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import ComponentLibrary from 'shared/widgets';
import type { SubcomponentEditor } from 'shared/components/EditorContext';

const EditorSlot = React.lazy(() => import(/* webpackChunkName: "internals" */ './EditorSlot'));

type Padding = {
  menu_bar_padding: boolean,
  use_mobile_padding: boolean,
  padding_container_top: string,
  padding_container_bottom: string,
  horizontal_outer_padding: string,
  horizontal_inner_padding: string,
  vertical_inner_padding: string,
  padding_all_tablet: {
    padding_container_top_tablet: string,
    padding_container_bottom_tablet: string,
    horizontal_outer_padding_tablet: string,
    horizontal_inner_padding_tablet: string,
    vertical_inner_padding_tablet: string,
  },
  padding_all_mobile: {
    padding_container_top_mobile: string,
    padding_container_bottom_mobile: string,
    horizontal_outer_padding_mobile: string,
    horizontal_inner_padding_mobile: string,
    vertical_inner_padding_mobile: string,
  },
}

type Props = {
  classes: Object,
  removeColumnSpace: boolean,
  paddingAll: Padding,
  useMobilePadding: string,
  mobileKeepDirection: boolean,
  horizontalOuterPadding: string,
  verticalInnerPadding: string,
  horizontalInnerPadding: string,
  layoutDirection: string,
  columnCount: number,
  columnVariant: string,
  isEditing: boolean,
  editor: SubcomponentEditor,
  parentId: string,
  component?: {
    _id: string,
    presetId?: string,
    props: Object,
    subcomponents: Array<Object>,
    widgetId: string
  },
  componentIndex: number,
  fillSpace: boolean
};

const FlexibleCell = (props: Props) => {
  const {
    classes: c,
    paddingAll,
    layoutDirection,
    columnCount,
    columnVariant,
    isEditing,
    component,
    componentIndex
  } = props;

  // Defines the flexBasis needed for each column variant.
  // The larger column is given a flex basis, and remaining space is divided evenly.
  const getColClass = (i) => {
    switch (columnVariant) {
      case '1:2':
        return i === 1 ? c.l3 : null;
      case '2:1':
        return i === 0 ? c.l3 : null;
      case '1:3':
        return i === 1 ? c.l5 : null;
      case '3:1':
        return i === 0 ? c.l5 : null;
      case '1:3:1':
        return i === 1 ? c.l4 : null;
      case '1:2:2':
        return i !== 0 ? c.l2 : null;
      case '2:2:1':
        return i !== 2 ? c.l2 : null;
      case '1:2:2:1':
        return (i !== 0 && i !== 3) ? c.l1 : null;
      // '1', '1:1', '1:1:1', '1:1:1:1'
      default:
        return null;
    }
  };

  const hasHorizontalOuterPadding = (paddingAll.horizontal_outer_padding || paddingAll.padding_all_tablet?.horizontal_outer_padding_tablet || paddingAll.padding_all_mobile?.horizontal_outer_padding_mobile);
  const paddingClasses = {
    [c.paddingLeftFirstCol]: ((layoutDirection === 'Single Column' || componentIndex === 0) && hasHorizontalOuterPadding),
    [c.paddingLeftOtherCol]: !((layoutDirection === 'Single Column' || componentIndex === 0) && hasHorizontalOuterPadding),
    [c.paddingRightLastCol]: ((layoutDirection === 'Single Column' || componentIndex === columnCount - 1) && hasHorizontalOuterPadding),
    [c.paddingRightOtherCol]: !((layoutDirection === 'Single Column' || componentIndex === columnCount - 1) && hasHorizontalOuterPadding),
    [c.paddingTopCol]: !(componentIndex === 0),
    [c.paddingBottomCol]: !(componentIndex === columnCount - 1),
    [c.paddingLeftAllCol]: true,
    [c.paddingRightAllCol]: true
  };


  // Render with editor controls
  if (isEditing) {
    const key = component ? component._id : componentIndex;

    return (
      <div
        className={classNames(
          props?.removeColumnSpace && c.removeColumnSpace,
          c.column,
          getColClass(componentIndex), paddingClasses
        )}
        key={key}
      >
        <EditorSlot {...props} />
      </div>
    );
  }

  if (component) {
    const Component = ComponentLibrary[component.widgetId];

    // Render just the component
    return (
      <div
        className={classNames(
          props?.removeColumnSpace && c.removeColumnSpace,
          c.column,
          getColClass(componentIndex), paddingClasses
        )}
        key={component._id}
      >
        <Component {...component.props} subcomponents={component.subcomponents} />
      </div>
    );
  }

  // Render the empty column, with Add Component in editor
  return (
    <div
      className={classNames(
        props?.removeColumnSpace && c.removeColumnSpace,
        c.column, getColClass(componentIndex),
        paddingClasses
      )}
      key={componentIndex}
    />
  );
};

const getInnerPadding = (totalPadding) => {
  if (/[0-9]+px/.test(totalPadding)) {
    return `${totalPadding.split('px')[0] / 2}px`;
  }
  if (/[0-9]+%/.test(totalPadding)) {
    return `${totalPadding.split('%')[0] / 2}%`;
  }

  return '0px';
};

// Only column view needs padding-top/bottom between elements
const getDesktopPaddingTopBottom = (props) => {
  const { paddingAll, layoutDirection } = props;
  const { vertical_inner_padding: verticalInnerPadding } = paddingAll;
  const splitVerticalPadding = getInnerPadding(verticalInnerPadding);

  return !(layoutDirection === 'Single Row') ? splitVerticalPadding : '0px';
};

// Only column view needs padding-top/bottom between elements
const getIpadPaddingTopBottom = (props) => {
  const { paddingAll, layoutDirection } = props;
  const { padding_all_tablet : paddingTablet } = paddingAll;
  const padding = paddingTablet?.vertical_inner_padding_tablet;

  if(!padding){
    return getDesktopPaddingTopBottom(props);
  }

  const splitVerticalPadding = getInnerPadding(padding);
  return !(layoutDirection === 'Single Row') ? splitVerticalPadding : '0px';
};

// Only column view needs padding-top/bottom between elements
const getMobilePaddingTopBottom = (props) => {
  const { paddingAll, mobileKeepDirection, layoutDirection } = props;
  const { padding_all_mobile: paddingAllMobile = {}, padding_all_tablet: paddingAllTablet = {}} = paddingAll;
  const { vertical_inner_padding_mobile: verticalInnerPaddingMobile } = paddingAllMobile;
  const { vertical_inner_padding_tablet: verticalInnerPaddingTablet } = paddingAllTablet;

  if (layoutDirection === 'Single Row' && mobileKeepDirection) {
    return '0px';
  }

  const padding = verticalInnerPaddingMobile || verticalInnerPaddingTablet || paddingAll.vertical_inner_padding || '0px';

  return getInnerPadding(padding);
};

// Check if consistent mobile padding is required
const getMobilePaddingLeftRight = (props) => {
  const { paddingAll } = props;
  const { padding_all_mobile: paddingAllMobile = {}, padding_all_tablet: paddingAllTablet = {}, use_mobile_padding: useMobilePadding } = paddingAll;
  const { horizontal_outer_padding_mobile: horizontalOuterPaddingMobile } = paddingAllMobile;
  const { horizontal_outer_padding_tablet: horizontalOuterPaddingTablet } = paddingAllTablet;

  if (useMobilePadding) {
    return '36px';
  }

  return horizontalOuterPaddingMobile || horizontalOuterPaddingTablet || paddingAll.horizontal_outer_padding || '0px';
};

// Check if consistent mobile padding is required
const getIpadOuterPaddingLeftRight = (props) => {
  const { paddingAll } = props;
  const { use_mobile_padding: useMobilePadding } = paddingAll;

  if (useMobilePadding) {
    return '36px';
  }

  return paddingAll.padding_all_tablet?.horizontal_outer_padding_tablet || getDesktopOuterPaddingLeftRight(props);
};

const getIpadInnerPaddingLeftRight = (props) => {
  const { paddingAll } = props;
  const { use_mobile_padding: useMobilePadding } = paddingAll;

  if (useMobilePadding) {
    // Total between left/right cells is 36px
    return '18px';
  }

  const padding = paddingAll.padding_all_tablet?.horizontal_inner_padding_tablet;
  if(!padding){
    return getDesktopInnerPaddingLeftRight(props);
  }

    return getInnerPadding(padding);
};

const getDesktopOuterPaddingLeftRight = (props) => {
  const { paddingAll } = props;
  const { horizontal_outer_padding: horizontalOuterPadding } = paddingAll;

  return horizontalOuterPadding || '0px';
};

const getDesktopInnerPaddingLeftRight = (props) => {
  const { paddingAll } = props;
  const { horizontal_inner_padding: horizontalInnerPadding } = paddingAll;
  const splitHorizontalPadding = getInnerPadding(horizontalInnerPadding);

  return splitHorizontalPadding || '0px';
};

// it's for Retaining same direction in mobile layout (don't convert to column)
const getMobileInnerPaddingLeftRight = (props) => {
  const { paddingAll, mobileKeepDirection } = props;
  if(!mobileKeepDirection){
    return null;
  }

  const padding = paddingAll.padding_all_mobile?.horizontal_inner_padding_mobile || paddingAll.padding_all_tablet?.horizontal_inner_padding || paddingAll.horizontal_inner_padding || '0px';

  return getInnerPadding(padding );
}

// react-slick needs a CSS fix to work in flexbox
// MondrianGrid: fix react slick going outside of the column
const reactSlickWidgets = ['VideoImageCarousel', 'FutureSightImage', 'MondrianGrid'];
const isReactSlickWidget = (widgetId) => reactSlickWidgets.includes(widgetId)

const styles = {
  column: {
    display: 'flex',
    justifyContent: 'center',
    flex: '1 1 0%',
    // Column content should use the whole width
    '&> *': {
      width: '100%',
      maxWidth: 'none'
    },
    // Fix for react-slick in flexbox
    // https://github.com/akiran/react-slick/issues/87#issuecomment-331786251
    minHeight: ({ component }) => isReactSlickWidget(component?.widgetId) ? '0' : null,
    minWidth: ({ component }) => isReactSlickWidget(component?.widgetId) ? '0' : null,

    // fix react-slick going infinitely wide outside of the column
    maxWidth: '100%',
    maxHeight: '100%',
  },
  removeColumnSpace: {
    // Add the inherit css to be able to align items and justify content.
    flex: 'inherit',
    // The cell will grow to fill the remaining space
    flexGrow: ({ fillSpace }) => fillSpace ? 1 : undefined,
    width: ({ fillSpace }) => fillSpace ? '100%' : undefined,
  },
  paddingLeftFirstCol: {
    // padding-left for the first column in a row OR any row in a column
    paddingLeft: getDesktopOuterPaddingLeftRight,
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingLeft: getIpadOuterPaddingLeftRight
    }
  },
  paddingRightLastCol: {
    // padding-right for last column in a row OR any row in a column
    paddingRight: getDesktopOuterPaddingLeftRight,
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingRight: getIpadOuterPaddingLeftRight
    }
  },
  paddingTopCol: {
    // padding-top for non-first rows in a column
    paddingTop: getDesktopPaddingTopBottom,
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingTop: getIpadPaddingTopBottom
    },
    '@media(max-width: 576px)': {
      paddingTop: getMobilePaddingTopBottom
    }
  },
  paddingBottomCol: {
    // padding-bottom for non-last rows in a column
    paddingBottom: getDesktopPaddingTopBottom,
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingBottom: getIpadPaddingTopBottom
    },
    '@media(max-width: 576px)': {
      paddingBottom: getMobilePaddingTopBottom
    }
  },
  'l1': {
    flexBasis: '16.6%'
  },
  'l2': {
    flexBasis: '20%'
  },
  'l3': {
    flexBasis: '33.3%'
  },
  'l4': {
    flexBasis: '40%'
  },
  'l5': {
    flexBasis: '50%'
  },
  paddingLeftAllCol: {
    '@media(max-width: 576px)': {
      paddingLeft: getMobilePaddingLeftRight
    },
  },
  paddingRightAllCol: {
    '@media(max-width: 576px)': {
      paddingRight: getMobilePaddingLeftRight
    },
  },
  paddingLeftOtherCol: {
    '@media(max-width: 576px)': {
      paddingLeft: getMobileInnerPaddingLeftRight
    },
    '@media(min-width: 769px) , (min-width: 577px) and (orientation: landscape)': {
      paddingLeft: getDesktopInnerPaddingLeftRight
    },
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingLeft: getIpadInnerPaddingLeftRight
    }
  },
  paddingRightOtherCol: {
    '@media(max-width: 576px)': {
      paddingRight: getMobileInnerPaddingLeftRight
    },
    '@media(min-width: 769px) , (min-width: 577px) and (orientation: landscape)': {
      paddingRight: getDesktopInnerPaddingLeftRight
    },
    '@media(max-width: 768px) and (min-width: 577px) and (orientation: portrait)': {
      paddingRight: getIpadInnerPaddingLeftRight
    }
  },
};

export default injectSheet(styles)(FlexibleCell);
